import classNames from 'classnames';
import qs from 'qs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from 'urql';
import { useStockOnHand } from '~/api/stock-on-hand';
import { AuthGate } from '~/components/AuthGate';
import { ListDetailView } from '~/components/ListDetailView';
import {
  ManageStockActions,
  ManageStockFAB,
} from '~/components/ManageStockActions';
import { StockManipulationComponent } from '~/components/smc/StockManipulationComponent';
import { TransferStock } from '~/components/stock/TransferStock';
import { StockOnHandTable } from '~/components/stock/stock-on-hand/StockOnHandTable';
import { Avatar } from '~/components/ui/Avatar';
import { FilterGroupSearch } from '~/components/ui/FilterGroupSearch';
import { NavContainer } from '~/components/ui/NavContainer';
import { StatefulView } from '~/components/ui/StatefulView';
import { VirtualList } from '~/components/ui/VirtualList';
import { SortBy, SortedBy } from '~/components/ui/sortBy';
import { View, ViewSwitcher } from '~/components/view-switcher/ViewSwitcher';
import { withModal } from '~/components/withModal';
import {
  ItemType,
  LocationType,
  Status,
  useLocationsQuery,
} from '~/generated/graphql';
import { getFragmentData } from '~/gql';
import { useLocalStorage } from '~/hooks';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useDisplayState } from '~/hooks/useDisplayState';
import { useDynamicHeaderMargin } from '~/hooks/useDynamicHeaderMargin';
import { useEnumOptions } from '~/hooks/useEnumOptions';
import { useSearch } from '~/hooks/useSearch';
import { StockNav } from '~/layouts/nav/StockNav';
import { StockOnHand as Stock } from '~/types';
import { ItemData_ItemFragment, ItemsQueryDocument } from './items';

const SMCModal = withModal(StockManipulationComponent);
const TransferStockModal = withModal(TransferStock);

const SEARCH_OPTIONS = {
  keys: [
    'locationType',
    'location',
    'itemType',
    'sku',
    'skuCode',
    'skuName',
    'locationName',
    'spaceName',
  ],
  silentErrors: true,
};

export const StockOnHand = () => {
  const localStorage = useLocalStorage();
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const [view, setView] = useState<View>('CARD');
  const navigate = useNavigate();
  const location = useLocation();

  // Get filter options early to avoid initialization errors
  const locationOptions = useLocationOptions();
  const spaceOptions = useSpaceOptions();
  const itemTypes = useEnumOptions(ItemType, 'itemType');
  const skuOptions = useSkuOptions();

  // State for filters and search
  const [searchValue, setSearchValue] = useState('');
  const [filtersValue, setFiltersValue] = useState<URLSearchParams>(() => {
    // Initialize filters from local storage if available
    try {
      const savedFilters = localStorage.get('stock-on-hand.filters');
      if (savedFilters) {
        return new URLSearchParams(savedFilters);
      }
    } catch (error) {
      console.error('Failed to load saved filters:', error);
    }
    return new URLSearchParams();
  });

  // Ref to track initial load vs subsequent loads
  const isInitialLoadRef = useRef(true);
  // State for sorting - Use imported SortedBy enum
  const [sortBy, setSortBy] = useState<SortedBy>(() => {
    const savedSort = localStorage.get('stock-on-hand.sort');
    return Object.values(SortedBy).includes(savedSort as SortedBy)
      ? (savedSort as SortedBy)
      : SortedBy.NAME; // Default to NAME (which is item.name:asc)
  });

  // Track previous query to prevent loops
  const prevQueryRef = useRef<string>('');
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Helper function to parse filters from URLSearchParams - defined early
  function parseStockFilters(params: URLSearchParams) {
    const result: Record<string, string[]> = {};

    // Only extract API-relevant filter parameters
    const relevantParams = ['location', 'space', 'itemType', 'skuId'];

    for (const param of relevantParams) {
      const values = params.getAll(param).filter((v) => v && v !== '');
      if (values.length > 0) {
        result[param] = values;
      }
    }

    return result;
  }

  // Convert filters to query parameters for API - defined early
  const getQueryString = useCallback(() => {
    // Similar to jobs.tsx, create a clean new object for filter params
    const filterParams: Record<string, any> = { sortBy };

    // Parse filters consistently - only care about API-relevant params
    const parsedFilters = parseStockFilters(filtersValue);

    // Assign parsed filter arrays directly to the params object
    if (parsedFilters.location && parsedFilters.location.length > 0) {
      filterParams.location = parsedFilters.location;
    }
    if (parsedFilters.space && parsedFilters.space.length > 0) {
      filterParams.space = parsedFilters.space;
    }
    if (parsedFilters.itemType && parsedFilters.itemType.length > 0) {
      filterParams.itemType = parsedFilters.itemType;
    }
    if (parsedFilters.skuId && parsedFilters.skuId.length > 0) {
      filterParams.skuId = parsedFilters.skuId;
    }

    // Create and return query string using 'repeat' array format
    return qs.stringify(filterParams, {
      arrayFormat: 'brackets',
      encode: true,
    });
  }, [filtersValue, sortBy]);

  // Get the current query string for API calls
  const [query, setQuery] = useState(() => getQueryString());

  // Effect to correctly format API request parameters from filters
  useEffect(() => {
    if (filtersValue) {
      try {
        // Save filters to local storage
        localStorage.set('stock-on-hand.filters', filtersValue.toString());

        // Don't call revalidate directly, as it causes an infinite loop
        // The query update will trigger a revalidation through the SWR hook
      } catch (error) {
        console.error('Error saving filters:', error);
      }
    }
  }, [filtersValue, localStorage]);

  // Update query when filters or sorting changes, but avoid unnecessary updates
  useEffect(() => {
    const newQuery = getQueryString();
    if (newQuery !== prevQueryRef.current) {
      prevQueryRef.current = newQuery;
      setQuery(newQuery);
    }
  }, [getQueryString]);

  // Fetch data with query
  const {
    data,
    mutate: revalidate,
    error,
    isValidating,
  } = useStockOnHand(query, { revalidateOnFocus: false });

  // Setup search early to avoid reference issues
  const { search, results } = useSearch(data, SEARCH_OPTIONS);

  // Use search results or all data
  const stock = useMemo(() => {
    // Return empty array if data is explicitly null (initial state before first fetch completes)
    if (data === null) return [];
    if (searchValue) return results?.filter((item) => !!item) ?? [];
    return data?.filter((item) => !!item) ?? [];
  }, [data, results, searchValue]);

  // Calculate activeFilters state
  const areFiltersActive = useMemo(() => {
    return Object.keys(parseStockFilters(filtersValue)).length > 0;
  }, [filtersValue]);

  // Ref to track initial load completion - managed by useDisplayState now
  // const initialLoadComplete = useRef(false); // Removed
  // Debounced state for table rendering
  const [isTableRendering, setIsTableRendering] = useState(false);
  const renderTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use the new useDisplayState hook
  const displayState = useDisplayState({
    isValidating,
    data,
    error,
    derivedData: stock,
    isInitialLoadRef,
  });

  // Use the combined hook
  const { filterRowRef, dynamicMarginTop } = useDynamicHeaderMargin();

  // Handle filter changes - simplified version
  const handleFilterGroupChange = useCallback(
    ({
      searchValue: newSearchValue,
      filtersValue: newFiltersValue,
    }: {
      searchValue: string;
      filtersValue: URLSearchParams;
    }) => {
      try {
        // Simply save the filters to localStorage and update state
        localStorage.set('stock-on-hand.filters', newFiltersValue.toString());
        setSearchValue(newSearchValue);
        setFiltersValue(newFiltersValue);

        if (newSearchValue) {
          search(newSearchValue);
        }
      } catch (error) {
        console.error('Error processing filters:', error);
      }
    },
    [search, localStorage]
  );

  const smcHandleSuccess = () => {
    navigate(-1);
    revalidate();
  };

  const smcHandleCancel = () => {
    navigate('/stock-on-hand');
  };

  // Use SortedBy type for the handler
  const handleSortChange = useCallback(
    (value: SortedBy) => {
      setSortBy(value);
      localStorage.set('stock-on-hand.sort', value);
    },
    [localStorage]
  );

  useEffect(() => {
    const navigateTo = localStorage.get('nav.items') ?? '/stock-on-hand';
    // Only navigate if the target is different from the current path
    // location.pathname added to dependencies
    // The stability of the `localStorage` object itself is still a potential concern for re-runs.
  }, [navigate, localStorage, location.pathname]);

  return (
    <AuthGate action='manage' subject='feat.items'>
      <NavContainer nav={<StockNav />}>
        <NavContainer.FilterRow ref={filterRowRef}>
          <ViewSwitcher selected={view} onSelect={setView} />{' '}
          <FilterGroupSearch
            filters={[
              {
                name: 'location',
                label: t('location'),
                options: locationOptions,
                type: 'select',
                multiple: true,
                searchable: true,
              },
              {
                name: 'space',
                label: t('space'),
                options: spaceOptions,
                type: 'select',
                multiple: true,
                searchable: true,
                // TODO: Remove this once we have a way to select all spaces without triggering a net::ERR_ABORTED 431 (Request Header Fields Too Large) error
                selectAll: false,
              },
              {
                name: 'itemType',
                label: t('itemTypeLabel'),
                options: itemTypes,
                type: 'select',
                multiple: true,
                searchable: true,
              },
              {
                name: 'skuId',
                label: t('sku'),
                options: skuOptions,
                type: 'select',
                multiple: true,
                searchable: true,
              },
            ]}
            value={{ searchValue, filtersValue }}
            onChange={handleFilterGroupChange}
            placeholder={'Find Stock...'}
            sortOptions={[
              { key: SortedBy.NAME, name: 'Item Name' },
              { key: SortedBy.SKU, name: 'Item SKU' },
              { key: SortedBy.LOCATION, name: 'Location' },
            ]}
            sortBy={sortBy}
            onSortChange={handleSortChange}
          />
          <SortBy
            className='ml-auto hidden lg:block'
            options={[
              { key: SortedBy.NAME, name: 'Item Name' },
              { key: SortedBy.SKU, name: 'Item SKU' },
              { key: SortedBy.LOCATION, name: 'Location' },
            ]}
            value={sortBy}
            onChange={handleSortChange}
          />
        </NavContainer.FilterRow>
      </NavContainer>
      <ListDetailView
        isMobile={isMobile}
        dynamicMarginTop={dynamicMarginTop ? dynamicMarginTop : undefined}
        nav={<StockNav />}
        actions={<ManageStockActions />}
        fetching={
          displayState === 'INITIAL_LOADING' || displayState === 'LOADING'
        }
        count={displayState === 'IDLE' ? stock?.length : undefined}
        disableMainScrollContainer={view === 'TABLE'}
        main={
          <StatefulView
            className={classNames(view === 'TABLE' ? 'h-full' : '')}
            displayState={displayState}
            activeFilters={areFiltersActive}
            isSearchResultEmpty={
              displayState === 'NO_RESULTS' &&
              !!searchValue &&
              !!data &&
              data.length > 0
            }
            entityName='Stock on hand'
          >
            {/* Content for IDLE state */}
            {view === 'CARD' ? (
              <VirtualList
                data={stock}
                getListItemProps={getListItemProps}
                placeholderIcon={(item) => (
                  <Avatar
                    name={item.skuName}
                    className='rounded'
                    size='full'
                    square
                  />
                )}
              />
            ) : (
              <div ref={tableContainerRef} className='h-full overflow-auto'>
                <StockOnHandTable
                  data={stock}
                  containerRef={tableContainerRef}
                  isLoading={isTableRendering}
                />
              </div>
            )}
          </StatefulView>
        }
      >
        <Route path='*' element={<ManageStockFAB />} />
        <Route
          path='stock-adjustment'
          element={
            <SMCModal
              mode='adjustment'
              onSuccess={smcHandleSuccess}
              onCancel={smcHandleCancel}
            />
          }
        />
        <Route
          path='stocktake'
          element={
            <SMCModal
              mode='stocktake'
              onSuccess={smcHandleSuccess}
              onCancel={smcHandleCancel}
            />
          }
        />
        <Route
          path='transfer-stock'
          element={<TransferStockModal returnTo='/stock-on-hand' />}
        />
      </ListDetailView>
    </AuthGate>
  );
};

const getListItemProps = (stock: Stock, scrollTo?: number | string | null) => ({
  id: stock.id,
  linkTo: `/stock-on-hand`,
  image: stock.image,
  titleText: stock.skuName,
  secondaryText: `${stock.locationName} - ${stock.spaceName}`,
  heroElement: (
    <div className='flex flex-col'>
      <p className='text-right text-sm leading-tight'>
        <span className='text-medium text-[17px] leading-tight'>
          {stock.soh}
        </span>
        {/* {stock.target ? ' / ' + stock : ''} */}
      </p>
      <p className='text-xs leading-5 text-grey-40'>On hand</p>
    </div>
  ),
  active: Boolean(scrollTo && '' + scrollTo === stock.id),
});

function useLocationOptions() {
  const [result] = useLocationsQuery({
    variables: { type: LocationType.Site },
  });
  const { data } = result;
  return data?.locations
    ? data.locations
        .filter((location) => location && location.id && location.name)
        .map(({ id, name }) => ({ value: `s:${id}`, label: name }))
    : [];
}

function useSpaceOptions() {
  const [result] = useQuery<{ spaceNames: string[] }>({
    query: '{ spaceNames }',
  });
  const { data } = result;
  return data?.spaceNames
    ? data.spaceNames
        .filter((value) => !!value)
        .map((value) => ({ value, label: value }))
    : [];
}

function useSkuOptions() {
  const [result] = useQuery({
    query: ItemsQueryDocument,
    variables: { status: Status.Active, inConfig: true },
  });
  const { data } = result;
  return data?.items
    ? data.items.flatMap((item) => {
        const { skus } = getFragmentData(ItemData_ItemFragment, item);

        return skus
          .filter((sku) => sku && sku.id && sku.name) // safeguard against nulls
          .map(({ id, name }) => ({ value: id, label: name }));
      })
    : [];
}
